<template>
  <div class="single-container" v-if="notDeleted">
    <div class="image-container">
      <router-link
        :to="{
          name: 'model.username',
          params: { username: '@' + data.user.username },
        }"
      >
        <CommentUserIcon :imageUrl="imageUrl" :logged="true" />
      </router-link>
    </div>

    <div class="comment-container">
      <div class="content-container">
        <div class="username-container">
          <router-link
            :to="{
              name: 'model.username',
              params: { username: '@' + data.user.username },
            }"
          >
            {{ data.user.username }}
          </router-link>
          <!-- <span>{{ data.published_at }}</span> -->
          <div
            class="comment-options dropdown-container"
            v-if="showOptionsBar"
            @mouseleave="hideOptions"
          >
            <div
              class="dropdown-fake-container"
              :class="{ active: showOptions }"
            >
              <div
                class="dropdown-btn icon fa-lg fas"
                :class="{ active: showOptions }"
                aria-hidden="true"
                @click="toggleOptions"
              ></div>
            </div>
            <div v-if="showOptions" class="dropdown-items">
              <div class="item" @click="handleDeleteComment">
                <template v-if="!deleteCommentInstance">
                  {{ $t("comments.options.delete") }}
                </template>
                <template v-else>
                  {{ $t("comments.options.confirm_delete") }}
                </template>
              </div>
            </div>
          </div>
        </div>
        <span v-html="message"></span>
        <span class="reply-container" @click="raiseReply">
          {{ $t("comments.placeholder.respond") }}
        </span>
      </div>

      <div class="replies-container" v-if="!nested">
        <CommentReplyList
          ref="replyList"
          :post="post"
          :model="model"
          :commentId="data.id"
          :replies="data.replies"
          @delete="deleteNestedComment"
          @reply="raiseNestedReply"
          @changeHeight="$emit('changeHeight')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Comment } from "~/types/Comment";
import { Post } from "~/types/Post";
import { Model } from "~/types/Model";
import { User } from "~/types/User";
import { _NuxtApp } from "~/types/NuxtApp";

import resources from "~/utils/resources";

const { $auth,  $i18n: { t: $t } } = <_NuxtApp>useNuxtApp();
const $emit = defineEmits(["reply", "changeHeight", "delete"]);

// Props
type Props = {
  data: Comment;
  model: Model;
  post: Post;
  nested?: boolean;
};
const { data, model, post, nested }: Props = withDefaults(
  defineProps<Props>(),
  {
    nested: false,
  }
);

// Data
const showOptions = ref(false);
const notDeleted = ref(true);
const deleteCommentInstance = ref(false);

// Refs
const replyList = ref(null);

// Computed
const imageUrl = data.user.profile_pic;
const user = computed((): Promise<User | null> => {
  if ($auth.ready() && $auth.check()) {
    return $auth.user();
  }

  return Promise.resolve({} as User);
});
const showOptionsBar = computed(() => {
  return user.value.id === data.user.id;
});
const message = computed(() => {
  return data.message.replace(
    /@([a-zA-Z0-9_-]+)/g,
    '<span class="reply-to">$&</span>'
  );
});

// Methods
const toggleOptions = () => {
  showOptions.value = !showOptions.value;

  if (!showOptions.value) resetOptionsConfig();
};
const hideOptions = () => {
  showOptions.value = false;
  resetOptionsConfig();
};
const resetOptionsConfig = () => {
  deleteCommentInstance.value = false;
};

const genericRaiseReply = (comment: Comment) => {
  if (nested) return $emit("reply", comment);

  $emit("reply", {
    comment: comment,
    cb: (comment: Comment) => {
      replyList.value.appendComment(comment);
      $emit("changeHeight");
    },
  });
};

const raiseReply = () => {
  genericRaiseReply(data);
};

const raiseNestedReply = (nestedComment: Comment) => {
  if (!nested) {
    nestedComment["id"] = data.id;
  }

  genericRaiseReply(nestedComment);
};

const handleDeleteComment = () => {
  if (deleteCommentInstance.value) {
    deleteComment();
    return;
  }

  deleteCommentInstance.value = true;
};

const deleteComment = () => {
  resources.comments
    .deleteComment(data.id)
    .then((response) => {
      if (!response.success) return;
      notDeleted.value = false;
      $emit("delete", data.id);
    })
    .catch((error) => {
      console.log("deleteComment: ", error);
    });
};

const deleteNestedComment = (commentId: number) => {
  data.replies -= 1;
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.single-container {
  display: flex;
  flex-direction: row;
  width: 100%;

  padding-block: 0 1em;

  .image-container {
    margin-right: 1em;
  }
}

.comment-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-block: unset;
  flex-grow: 1;

  .replies-container {
    display: flex;
    flex-direction: column;
    margin-top: 1em;
    width: 100%;

    > .replies-show-btn {
      display: flex;
      flex-direction: row;
      font-size: 0.9em;
      font-weight: 500;
      color: var(--pallete_dark_text_color);

      -webkit-tap-highlight-color: transparent;
      background: none;

      > .replies-decorator {
        height: 1px;
        width: 2.5em;
        border-top: 1px solid var(--pallete_dark_text_color);
        margin-block: auto;
        margin-right: 0.5em;
      }
    }
  }

  .content-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    .reply-container {
      font-size: 0.9em;
      font-weight: 500;
      color: var(--pallete_dark_text_color);
      width: fit-content;
      // color: var(--pallete_primary_color);
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }

      -webkit-tap-highlight-color: transparent;
      background: none;
    }
  }
}

.username-container {
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;

  a {
    font-size: 1em;
    margin: 0;
    font-weight: bold;
  }

  span {
    font-size: 0.8em;
    margin-left: 0.5em;
    margin-block: auto;
  }
}

.comment-options {
  align-self: center;
  margin-right: 1em;

  .icon {
    width: 20px;
    height: 20px;
    padding-bottom: 0.75em;
    padding-inline: 0.5em;
    background-image: url("/img/btn/three-dots.png");
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
  }
}

.dropdown-container {
  position: relative;
  right: 0%;

  .item,
  .dropdown-fake-container.active {
    min-width: 150px;
    text-align: center;
    color: inherit;
    text-decoration: none;
    text-transform: uppercase;
    font-weight: bold;
    padding-block: 0.75em;
    flex-grow: 1;
    transition: background-color 0.2s ease-in-out;

    &.router-link-active {
      background-color: lighten(#000000, 60);
    }

    &:hover {
      background-color: lighten(#000000, 75);
    }
  }

  .item {
    display: block;
  }

  .dropdown-fake-container {
    display: flex;
    justify-content: flex-end;

    &.active {
      padding-block: 0em;
      background-color: unset !important;
    }
  }

  .dropdown-btn {
    cursor: pointer;

    &.active {
      cursor: default;
    }
  }

  .dropdown-items {
    position: absolute;
    top: 100%;
    right: 0%;
    z-index: 9;
    background: lighten(#000000, 90);

    &:hover {
      cursor: pointer;
    }

    &::selection {
      background: none;
    }
  }
}
</style>

<style lang="scss">
@import "@/assets/scss/variables.scss";

.comment-container > .content-container > span {
  margin-right: 40px;
  overflow-wrap: anywhere;

  > .reply-to {
    color: var(--pallete_main_color);
  }
}
</style>
